import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import i18n from "i18next";
import { ChevronsUpDown, Check, Plus, Loader2, Building2, X } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Workspace switcher — sidebar dropdown. Lets the user jump between any
 * workspace they belong to, or spin up a brand-new one ("Voeg workspace toe").
 */
export default function WorkspaceSwitcher() {
    const { t } = useTranslation();
    const { workspace, memberships, switchWorkspace, user } = useAuth();
    const [open, setOpen] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const ref = useRef(null);

    // close on outside click
    useEffect(() => {
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const select = async (ws_id) => {
        if (ws_id === workspace?.id) { setOpen(false); return; }
        setBusyId(ws_id);
        try {
            await switchWorkspace(ws_id);
            toast.success("Workspace gewisseld.");
            setOpen(false);
            // Reload window to clear any in-memory page caches scoped to old workspace
            setTimeout(() => window.location.reload(), 200);
        } catch (e) {
            toast.error(e?.response?.data?.detail || i18n.t("wsw.switch_failed"));
        } finally { setBusyId(null); }
    };

    const list = memberships || [];

    return (
        <>
            <div ref={ref} className="relative w-full">
                <button onClick={() => setOpen((x) => !x)} type="button"
                    data-testid="workspace-switcher-trigger"
                    className="w-full text-left rounded-lg border border-white/10 bg-[var(--jr-surface)] hover:bg-white/[0.04] px-3 py-2.5 flex items-center gap-2 transition-colors">
                    <div className="w-7 h-7 rounded-md bg-[var(--jr-brand)]/15 grid place-items-center text-[var(--jr-brand)] shrink-0">
                        <Building2 size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{t("nav.workspace")}</div>
                        <div className="text-sm text-white truncate">{workspace?.name || "…"}</div>
                    </div>
                    <ChevronsUpDown size={14} className="text-[var(--jr-text-2)] shrink-0" />
                </button>

                {open && (
                    <div data-testid="workspace-switcher-menu"
                        className="absolute left-0 right-0 mt-1.5 z-50 rounded-lg border border-white/10 bg-[var(--jr-surface-2)] shadow-[0_15px_40px_-15px_rgba(0,0,0,0.7)] py-1 overflow-hidden jr-fadein">
                        <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--jr-text-3)]">
                            {user?.email}
                        </div>
                        <ul className="max-h-[220px] overflow-y-auto">
                            {list.length === 0 ? (
                                <li className="px-3 py-2 text-xs text-[var(--jr-text-3)] italic">{i18n.t("wsw.none_found")}</li>
                            ) : list.map((m) => {
                                const isActive = m.workspace_id === workspace?.id;
                                const isBusy = busyId === m.workspace_id;
                                return (
                                    <li key={m.workspace_id}>
                                        <button onClick={() => select(m.workspace_id)} type="button"
                                            disabled={isBusy}
                                            data-testid={`workspace-option-${m.workspace_id}`}
                                            className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-white/5 transition-colors ${isActive ? "bg-white/[0.04]" : ""}`}>
                                            <div className="w-6 h-6 rounded-md grid place-items-center text-xs"
                                                style={{ background: "var(--jr-brand)" + "1F", color: "var(--jr-brand)" }}>
                                                {m.workspace_name?.[0]?.toUpperCase() || "W"}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm text-white truncate">{m.workspace_name}</div>
                                                <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">{m.role} · {m.workspace_tier}</div>
                                            </div>
                                            {isBusy ? <Loader2 size={14} className="animate-spin text-[var(--jr-text-2)]" /> :
                                                isActive ? <Check size={14} className="text-[var(--jr-brand)]" /> : null}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                        <div className="border-t border-white/8 mt-1 pt-1">
                            <button type="button" onClick={() => { setOpen(false); setShowAdd(true); }}
                                data-testid="workspace-add-trigger"
                                className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-white/5 text-sm text-[var(--jr-text-2)] hover:text-white">
                                <Plus size={14} /> Nieuwe workspace…
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {showAdd && <AddWorkspaceModal onClose={() => setShowAdd(false)} />}
        </>
    );
}

function AddWorkspaceModal({ onClose }) {
    const { user, addWorkspace } = useAuth();
    const nav = useNavigate();
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [tier, setTier] = useState("solo");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setBusy(true);
        try {
            await addWorkspace({ email: user.email, password, workspace_name: name.trim(), tier });
            toast.success(i18n.t("wsw.created", { name }));
            onClose();
            setTimeout(() => { nav("/app/dashboard"); window.location.reload(); }, 250);
        } catch (e) {
            toast.error(e?.response?.data?.detail || i18n.t("companies.create_failed"));
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={(e) => e.target === e.currentTarget && onClose()}>
            <form onSubmit={submit}
                className="w-full max-w-md bg-[var(--jr-surface)] border border-white/10 rounded-2xl p-6 jr-fadein"
                data-testid="add-workspace-modal">
                <div className="flex items-center justify-between mb-1">
                    <h3 className="font-display text-2xl tracking-tight">{i18n.t("wsw.new_ws")}</h3>
                    <button type="button" onClick={onClose} className="text-[var(--jr-text-2)] hover:text-white"><X size={20} /></button>
                </div>
                <p className="text-xs text-[var(--jr-text-2)] mb-4">{i18n.t("wsw.stay_logged_pre")} <span className="text-white">{user?.email}</span>. {i18n.t("wsw.stay_logged_post")}</p>
                <div className="space-y-3">
                    <Field label={i18n.t("wsw.lbl_name")} value={name} onChange={setName}
                        placeholder={i18n.t("wsw.name_ph")} required testid="add-ws-name" />
                    <Field label="Tier" select value={tier} onChange={setTier}
                        options={[
                            ["solo", "Solo — 500 credits"],
                            ["team", "Team — 2.500 credits"],
                            ["agency", "Agency — 10.000 credits"],
                        ]} testid="add-ws-tier" />
                    <Field label={i18n.t("wsw.lbl_confirm_pw")} type="password" value={password} onChange={setPassword}
                        placeholder={i18n.t("wsw.pw_ph")} required testid="add-ws-password" />
                </div>
                <div className="mt-5 flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-white/10 text-sm text-[var(--jr-text-2)] hover:bg-white/5">{i18n.t("common.cancel")}</button>
                    <button type="submit" disabled={busy} data-testid="add-ws-submit"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[var(--jr-brand)] text-white text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-60">
                        {busy ? <><Loader2 className="animate-spin" size={14} /> {i18n.t("wsw.creating")}</> : i18n.t("wsw.make_ws")}
                    </button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, value, onChange, placeholder, type = "text", required, testid, select, options }) {
    return (
        <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{label}{required && " *"}</label>
            {select ? (
                <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
                    className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
                    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
            ) : (
                <input value={value} onChange={(e) => onChange(e.target.value)} type={type} required={required}
                    placeholder={placeholder} data-testid={testid}
                    className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none" />
            )}
        </div>
    );
}
